import { CKDrawer } from 'clearkit';
import snakeCase from 'lodash/snakeCase';

import LoadingArea from '~/components/LoadingArea';
import ToastsContainer from '~/containers/Toasts';
import { useHasLegacyPlan } from '~/hooks/account/usePlatformAccess';
import { useFancyQuery } from '~/lib/graphql';
import history from '~/lib/history';
import { trackUpgradeFormSubmitted } from '~/lib/segmentAnalytics.js';

import LegacyUpgradeAccountForm, {
  isValid as legacyIsValid,
} from './LegacyUpgradeAccountForm';
import query from './query.graphql';
import styles from './styles';
import UpgradeAccountComplete from './UpgradeAccountComplete';
import UpgradeAccountForm, {
  addonOptions,
  isValid,
} from './UpgradeAccountForm';
import useForm from './useForm';

const formValues = {
  dataEnrichment: [
    'enrichment_salesforce',
    'enrichment_hubspot',
    'enrichment_marketo',
    'enrichment_segment',
  ],
  advertising: ['alerting', 'advertising', 'personalization'],
  platform: addonOptions,
};

function UpgradeAccount({
  name,
  email,
  onSubmit,
  submitted,
  product,
  ...props
}) {
  const hasLegacyPlan = useHasLegacyPlan();
  let initialValues = { name, email };
  const validate = hasLegacyPlan ? legacyIsValid : isValid;

  if (!hasLegacyPlan) {
    formValues.platform.forEach((i) => {
      initialValues[i] = false;
    });
  }

  if (product) {
    initialValues[product] = true;
  }

  const { values, onChange, valid } = useForm({
    initialValues,
    isValid: validate,
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    onSubmit(values);
  };

  return (
    <>
      <CKDrawer.Header>
        <CKDrawer.Heading>Access the full Clearbit experience</CKDrawer.Heading>

        <div>
          Contact our success team to access additional Clearbit capabilities.
        </div>
      </CKDrawer.Header>
      {submitted ? (
        <UpgradeAccountComplete email={values.email} {...props} />
      ) : hasLegacyPlan ? (
        <LegacyUpgradeAccountForm
          onChange={onChange}
          onSubmit={handleSubmit}
          product={product}
          valid={valid}
          values={values}
          {...props}
        />
      ) : (
        <UpgradeAccountForm
          onChange={onChange}
          onSubmit={handleSubmit}
          product={product}
          valid={valid}
          values={values}
          {...props}
        />
      )}
    </>
  );
}

function mapProductToUpgradeRequests(productName, hasLegacyPlan) {
  if (hasLegacyPlan) {
    if (productName === 'dataEnrichment') {
      return 'dataEnrichment';
    } else {
      return 'advertising';
    }
  } else {
    return snakeCase(productName);
  }
}

const ConnectedUpgradeAccount = (props) => {
  const hasLegacyPlan = useHasLegacyPlan();
  const upgradeRequest = mapProductToUpgradeRequests(
    props.product,
    hasLegacyPlan,
  );
  const { backPath } = props;
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [user, setUser] = useState({});
  const product = hasLegacyPlan ? upgradeRequest : 'platform';

  const handleSubmit = async (values) => {
    const productString = formValues[product]
      .filter((key) => values[key])
      .join(', ');

    const { email, name, message } = values;

    trackUpgradeFormSubmitted(email, name, message, productString);
    setSubmitted(true);
  };

  const { data, loading: loadingGraphQL, error } = useFancyQuery(query);

  useEffect(() => {
    if (!loadingGraphQL || error) {
      setUser(data.viewer.user);
      setLoading(false);
    }
  }, [data, error, loadingGraphQL]);

  useEffect(() => {
    if (error) {
      ToastsContainer.addError('There was an error loading the form.');
    }
  }, [error]);

  if (loading) {
    return <LoadingArea loading />;
  }

  return (
    <UpgradeAccount
      email={user.email}
      error={error}
      name={user.name}
      onClose={() => {
        history.push(backPath);
      }}
      onSubmit={handleSubmit}
      submitted={submitted}
      {...props}
    />
  );
};

ConnectedUpgradeAccount.propTypes = {
  classes: PropTypes.object.isRequired,
  backPath: PropTypes.string,
  product: PropTypes.string,
};

ConnectedUpgradeAccount.defaultProps = {
  product: 'advertising',
};

export default withStyles(styles)(ConnectedUpgradeAccount);
